import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ShoppingListItem, ShoppingListSort, UserProfile } from '@recipes/contracts';
import { api } from '@/lib/api';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { queryKeys } from '@/lib/query-keys';
import { localShoppingPreferences, storeShoppingPreferences } from './device-copy';
import { useShoppingPreferences } from './hooks';

/**
 * How a shopping list is laid out: by department (the default – in the order the store is walked
 * through), alphabetically, or in the order the items were added. The choice belongs to the user
 * (`shoppingPreferences.listSort`) and follows them to other devices; which departments are
 * folded belongs to the device and the list.
 */

/** Open items in one flat list. By department the page groups them itself (`groupItems`). */
export function sortItems(
  items: ShoppingListItem[],
  sort: Exclude<ShoppingListSort, 'DEPARTMENT'>,
  locale: string,
): ShoppingListItem[] {
  const byEntry = (a: ShoppingListItem, b: ShoppingListItem) => a.position - b.position;
  if (sort === 'ADDED') return [...items].sort(byEntry);
  // "Äpfel" next to "Apfel", "2 kg Mehl" under M: the name decides, never the amount.
  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
  return [...items].sort((a, b) => collator.compare(a.name, b.name) || byEntry(a, b));
}

// --- the user's sort order -------------------------------------------------------------------

const PENDING_KEY = 'rm.shopping.listSort.pending';

const readPending = (): ShoppingListSort | null => {
  try {
    const value = localStorage.getItem(PENDING_KEY);
    return value === 'DEPARTMENT' || value === 'ALPHABETICAL' || value === 'ADDED' ? value : null;
  } catch {
    return null;
  }
};
const writePending = (value: ShoppingListSort | null) => {
  try {
    if (value) localStorage.setItem(PENDING_KEY, value);
    else localStorage.removeItem(PENDING_KEY);
  } catch {
    // storage unavailable: the choice still applies to this page
  }
};

/**
 * The sort order and its setter. A change applies at once – also without a connection, in the
 * store – and is saved to the profile now or when the connection is back.
 */
export function useListSort(): [ShoppingListSort, (sort: ShoppingListSort) => void] {
  const preferences = useShoppingPreferences();
  const queryClient = useQueryClient();
  const online = useOnlineStatus();
  const [pending, setPending] = useState<ShoppingListSort | null>(readPending);

  const save = useCallback(
    async (sort: ShoppingListSort) => {
      try {
        const profile = await api.users.updateProfile({ shoppingPreferences: { listSort: sort } });
        queryClient.setQueryData(queryKeys.profile, profile);
        await storeShoppingPreferences(profile.shoppingPreferences);
        // Only forget the choice that was saved – another one may have been made meanwhile.
        if (readPending() === sort) {
          writePending(null);
          setPending(null);
        }
      } catch {
        // stays pending; the next visit with a connection tries again
      }
    },
    [queryClient],
  );

  useEffect(() => {
    if (online && pending && pending !== preferences.listSort) void save(pending);
    else if (pending && pending === preferences.listSort) {
      writePending(null);
      setPending(null);
    }
  }, [online, pending, preferences.listSort, save]);

  const choose = useCallback(
    (sort: ShoppingListSort) => {
      writePending(sort);
      setPending(sort);
      // The device copy answers when the profile cannot: keep it in step.
      void localShoppingPreferences().then((local) =>
        storeShoppingPreferences({ ...local, listSort: sort }),
      );
      queryClient.setQueryData<UserProfile | undefined>(queryKeys.profile, (profile) =>
        profile
          ? { ...profile, shoppingPreferences: { ...profile.shoppingPreferences, listSort: sort } }
          : profile,
      );
      if (navigator.onLine) void save(sort);
    },
    [queryClient, save],
  );

  return [pending ?? preferences.listSort, choose];
}

// --- folded departments ------------------------------------------------------------------------

const collapsedKey = (listId: string) => `rm.shopping.collapsed.${listId}`;

function readCollapsed(listId: string): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(collapsedKey(listId)) ?? '[]') as unknown;
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Which departments of a list are folded on this device. Folding is a way of looking at the
 * list while shopping ("done with the vegetables"), not a setting – it stays with the device.
 */
export function useCollapsedDepartments(listId: string) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(readCollapsed(listId)));
  useEffect(() => setCollapsed(new Set(readCollapsed(listId))), [listId]);

  const update = useCallback(
    (change: (current: Set<string>) => Set<string>) =>
      setCollapsed((current) => {
        const next = change(current);
        try {
          if (next.size) localStorage.setItem(collapsedKey(listId), JSON.stringify([...next]));
          else localStorage.removeItem(collapsedKey(listId));
        } catch {
          // storage unavailable: folded for this page view only
        }
        return next;
      }),
    [listId],
  );

  return {
    collapsed,
    toggle: useCallback(
      (category: string) =>
        update((current) => {
          const next = new Set(current);
          if (!next.delete(category)) next.add(category);
          return next;
        }),
      [update],
    ),
    /** An item that was just added or changed must be seen: its department opens. */
    expand: useCallback(
      (category: string) =>
        update((current) => {
          if (!current.has(category)) return current;
          const next = new Set(current);
          next.delete(category);
          return next;
        }),
      [update],
    ),
    collapseAll: useCallback((categories: string[]) => update(() => new Set(categories)), [update]),
    expandAll: useCallback(() => update(() => new Set()), [update]),
  };
}
